/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react'
import Card from '../../../common/Card'
import MemberInfo from '../../../common/MemberInfo'
import $t from '../../../../i18n'
import { languages, relationship } from '../../../../consts'
import MaterialInput from '../../../common/MaterialInput'
import MaterialSelectWithDropdown from '../../../common/MaterialSelectWithDropdown/MaterialSelectWithDropdown'
import { Checkbox } from 'antd'
import RelationTag from '../../../common/RelationTag'
import validateZipcode from 'utils/validateZipcode'
import ZipcodeValidationField from 'components/common/ZipcodeValidationField'
import './MemberDetailsCard.css'

const MemberDetailsCard = ({
  memberErrors,
  member,
  onChange = () => {},
  empty
}) => {
  const isMySelf = member?.relation === relationship.owner
  const shouldShowFakessn = member?.ssnLast4 && !member.ssn

  const languageList = Object.values(languages)?.map(lang => ({
    value: lang,
    label: $t(`common.languages.${lang}`)
  }))

  const onFieldChange = (key, value) => {
    onChange({ ...member, [key]: value })
  }

  const onAddressFieldChange = (key, value, addressType) => {
    onChange({
      ...member,
      [addressType]: { ...member?.[addressType], [key]: value }
    })
  }

  const onZipCodeChange = ({ zipCode, addressType }) => {
    onChange({
      ...member,
      [addressType]: {
        ...member?.[addressType],
        zipCode,
        state: validateZipcode(zipCode) || null
      }
    })
  }

  const getAddressFields = addressType => {
    return (
      <>
        <div className='row wrap'>
          <MaterialInput
            label={$t('fields.address1') + '*'}
            errorText={memberErrors?.[addressType]?.address1}
            value={member?.[addressType]?.address1}
            onChange={e =>
              onAddressFieldChange('address1', e.target.value, addressType)
            }
          />
          <MaterialInput
            label={$t('fields.address2')}
            value={member?.[addressType]?.address2}
            onChange={e =>
              onAddressFieldChange('address2', e.target.value, addressType)
            }
          />
        </div>
        <div className='row wrap'>
          <MaterialInput
            label={$t('fields.city')}
            errorText={memberErrors?.[addressType]?.city}
            value={member?.[addressType]?.city}
            onChange={e =>
              onAddressFieldChange('city', e.target.value, addressType)
            }
          />
          {isMySelf && addressType === 'homeAddress' && (
            <ZipcodeValidationField
              memberId={member?.id}
              errors={{
                zipCode: memberErrors?.homeAddress?.zipCode,
                county: memberErrors?.homeAddress?.county
              }}
              disabled
            />
          )}
          {(!isMySelf || addressType !== 'homeAddress') && (
            <MaterialInput
              label={$t('fields.zip')}
              errorText={memberErrors?.[addressType]?.zipCode}
              value={member?.[addressType]?.zipCode}
              onChange={e => {
                onZipCodeChange({ zipCode: e?.target?.value, addressType })
              }}
            />
          )}
        </div>
        <div className='row wrap'>
          <MaterialInput
            label={$t('fields.state')}
            value={member?.[addressType]?.state}
            disabled={true}
          />
        </div>
      </>
    )
  }

  const renderMemberDetails = () => {
    return (
      <>
        <div className='details-container'>
          <div className='field-title'>
            {isMySelf
              ? $t('enroll.details.myDetails')
              : $t('enroll.details.relationDetails', {
                  relation: $t('common.' + member?.relation)
                })}
            :
          </div>
          <div className='row wrap'>
            <MaterialInput
              label={$t('fields.firstName')}
              errorText={memberErrors?.firstName}
              value={member?.firstName}
              onChange={e => onFieldChange('firstName', e.target.value)}
            />
            <MaterialInput
              label={$t('fields.lastName')}
              errorText={memberErrors?.lastName}
              value={member?.lastName}
              onChange={e => onFieldChange('lastName', e.target.value)}
            />
          </div>
          <div className='row wrap'>
            <MaterialInput
              label={$t('fields.emailAddress')}
              errorText={memberErrors?.email}
              value={member?.email}
              onChange={e => onFieldChange('email', e.target.value)}
            />
            <MaterialInput
              label={$t('fields.phoneNumber')}
              errorText={memberErrors?.phone}
              type='phone'
              country='US'
              value={member?.phone}
              onChange={e => onFieldChange('phone', e)}
            />
          </div>
          <div className='row wrap'>
            <MaterialSelectWithDropdown
              className='languages'
              value={member?.prefferedLanguage}
              errorText={memberErrors?.prefferedLanguage}
              onChange={value => onFieldChange('prefferedLanguage', value)}
              data={languageList}
              label={$t('fields.language')}
            />
            <MaterialInput
              type='ssn'
              label={$t('fields.ssn')}
              ssnLast4={member?.ssnLast4}
              errorText={memberErrors?.ssn}
              value={member?.ssn}
              onChange={ssn => onFieldChange('ssn', ssn)}
              clearLast4={() => onFieldChange('ssnLast4', null)}
              shouldShowFakessn={shouldShowFakessn}
              forceMinimizeLabel={shouldShowFakessn}
            />
          </div>
        </div>
        <MemberInfo
          member={member}
          errors={memberErrors}
          isAnotherMember={!isMySelf}
          hideRelation={true}
          hideTitle={true}
          onChange={onFieldChange}
        />
        {isMySelf && (
          <div className='details-container'>
            <div className='field-header semi-bold'>
              {$t('enroll.details.contactInfo')}
            </div>
            <div className='field-title'>
              {$t('enroll.details.homeAddress')}:
            </div>
            {getAddressFields('homeAddress')}
            <Checkbox
              className='mailing-checkbox address-checkbox space'
              onChange={e =>
                onFieldChange(
                  'isMailingAddressAsHomeAddress',
                  e?.target?.checked
                )
              }
              checked={member?.isMailingAddressAsHomeAddress}
            >
              {$t('enroll.details.useHomeAddress')}
            </Checkbox>
            {!member?.isMailingAddressAsHomeAddress && (
              <>
                <div className='field-title'>
                  {$t('enroll.details.mailingAddress')}:
                </div>
                {getAddressFields('mailingAddress')}
              </>
            )}
          </div>
        )}
        {!isMySelf && (
          <div className='details-container'>
            <div className='field-title space'>
              {$t('enroll.details.homeAddress')}:
            </div>
            <Checkbox
              className='home-checkbox address-checkbox'
              onChange={e =>
                onFieldChange('isHomeAddressAsOwner', e?.target?.checked)
              }
              checked={member?.isHomeAddressAsOwner}
            >
              {$t('enroll.details.sameHomeAddress')}
            </Checkbox>
            {!member?.isHomeAddressAsOwner && getAddressFields('homeAddress')}
            <div className='field-title'>
              {$t('enroll.details.mailingAddress')}:
            </div>
            <Checkbox
              className='mailing-checkbox address-checkbox'
              onChange={e =>
                onFieldChange('isMailingAddressAsOwner', e?.target?.checked)
              }
              checked={member?.isMailingAddressAsOwner}
            >
              {$t('enroll.details.sameMailingAddress')}
            </Checkbox>
            {!member?.isMailingAddressAsOwner &&
              getAddressFields('mailingAddress')}
          </div>
        )}
      </>
    )
  }

  return (
    <Card className='member-details-card' id={member?.id}>
      <RelationTag relation={member?.relation} />
      {empty &&
        $t('enroll.details.noCoverage', {
          relation: $t('common.' + member?.relation)
        })}
      {!empty && renderMemberDetails()}
    </Card>
  )
}

export default MemberDetailsCard
